<template>
    <div class="w-full h-full flex flex-col p-3 pt-4 space-y-2">
        <div class="flex justify-center h-full">
            <ag-grid-vue
                class="w-full h-full max-w-screen-2xl"
                :pinned-top-row-data="pinnedTopRowData"
                :row-data="agGridRows"
                :column-defs="colDefs"
                :theme="agTheme"
                :grid-options="gridOptions"
            />
        </div>
        <simple-pager
            v-model="page"
            @update:model-value="fetchData"
        />
    </div>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, Ref, ref, shallowRef} from 'vue'
import {fetchAgeDividedReport, useSubAccounts} from '@/subaccount/subAccount.service'
import {AgeDividedReportEntry, CUSTOMER, SubAccountRelationType} from '@/subaccount/types/SubAccount'
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {formatCurrency} from '@/services/formatter.service'
import SubAccountNameCell from '@/subaccount/components/SubAccountNameCell.vue'
import {DELETE_TRANSACTION, SACC_LINES_UPDATED, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import {GridApi, GridOptions, GridReadyEvent, RowClassParams} from 'ag-grid-community'
import SimplePager from '@/components/SimplePager.vue'

const report = ref<AgeDividedReportEntry[]>()
const gridApi = shallowRef<GridApi>()
const page = ref()
const recordOfSums = ref<Record<string, any>>({})

function calculateSum() {
    recordOfSums.value = {
        'Not due': 0,
        '< 15': 0,
        '15-30': 0,
        '30-60': 0,
        '60-90': 0,
        '90-180': 0,
        '> 180': 0,
        'total': 0
    }

    agGridRows.value?.forEach((entry: Record<string, any>) => {
        if (entry['Not due']) {
            recordOfSums.value['Not due'] += parseInt(entry['Not due'])
        }
        if (entry['< 15']) {
            recordOfSums.value['< 15'] += parseInt(entry['< 15'])
        }
        if (entry['15-30']) {
            recordOfSums.value['15-30'] += parseInt(entry['15-30'])
        }
        if (entry['30-60']) {
            recordOfSums.value['30-60'] += parseInt(entry['30-60'])
        }
        if (entry['60-90']) {
            recordOfSums.value['60-90'] += parseInt(entry['60-90'])
        }
        if (entry['90-180']) {
            recordOfSums.value['90-180'] += parseInt(entry['90-180'])
        }
        if (entry['> 180']) {
            recordOfSums.value['> 180'] += parseInt(entry['> 180'])
        }
        if (entry['total']) {
            recordOfSums.value['total'] += parseInt(entry['total'])
        }
    })
    return null
}

function percentageOfTotalSum(value2: number, value1: number) {
    return ((value2 / value1) * 100)
}

const pinnedTopRowData = computed(() => {

    calculateSum()
    if (!recordOfSums.value) {
        return
    }
    return [
        {
            name: 'Sum',
            type: 'summary',
            valueFormatter,
            'Not due': recordOfSums?.value['Not due'],
            '< 15': recordOfSums?.value['< 15'],
            '15-30': recordOfSums?.value['15-30'],
            '30-60': recordOfSums?.value['30-60'],
            '60-90': recordOfSums?.value['60-90'],
            '90-180': recordOfSums?.value['90-180'],
            '> 180': recordOfSums?.value['> 180'],
            'total': recordOfSums?.value['total']
        },
        {
            name: 'Prosentandel',
            type: 'percentage',
            'Not due': percentageOfTotalSum(recordOfSums?.value['Not due'], recordOfSums?.value['total']) | 0,
            '< 15': (percentageOfTotalSum(recordOfSums?.value['< 15'], recordOfSums?.value['total'])) | 0,
            '15-30': percentageOfTotalSum(recordOfSums?.value['15-30'], recordOfSums?.value['total']) | 0,
            '30-60': percentageOfTotalSum(recordOfSums?.value['30-60'], recordOfSums?.value['total']) | 0,
            '60-90': percentageOfTotalSum(recordOfSums?.value['60-90'], recordOfSums?.value['total']) | 0,
            '90-180': percentageOfTotalSum(recordOfSums?.value['90-180'], recordOfSums?.value['total']) | 0,
            '> 180': percentageOfTotalSum(recordOfSums?.value['> 180'], recordOfSums?.value['total']) | 0,
            'total': percentageOfTotalSum(recordOfSums?.value['total'], recordOfSums?.value['total']) | 0,
        }
    ]
})


const agGridRows = computed(() => {
    if (!report.value)
        return
    return report.value.map((entry: AgeDividedReportEntry) => {
        const sa = subAccounts.value.find(s => s.uuid === entry.subAccountUuid)
        return {
            ...sa,
            ...entry.sums
        }
    })
})

const {onEvents, cleanUp} = useEmitter()

const props = defineProps({
    relationType: {type: Object as () => SubAccountRelationType, required: true}
})

const panelId: Ref<number> | undefined = inject('panelId')

const subAccounts = props.relationType === CUSTOMER ? useSubAccounts().customers : useSubAccounts().suppliers

const gridOptions: GridOptions = {
    context: {
        panelId
    },
    animateRows: true,
    getRowId: (params: any) => params.data.uuid,
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
        window.gridApi = gridApi.value
    },
    getRowClass: (params: RowClassParams) => {
        if (['summary'].includes(params.data?.type)) {
            return '!bg-base-100'
        }
        if (['percentage'].includes(params.data?.type)) {
            return '!bg-base-100 !border-base-300'
        }
    }
}

const valueFormatter = (params: any) => formatCurrency(params.value ?? 0, 0)
const defaultSize = {flex: 1, minWidth: 100, maxWidth: 260}
const colDefs = ([
    {
        headerName: props.relationType === CUSTOMER ? 'Kunde' : 'Leverandør',
        field: 'name',
        cellRenderer: SubAccountNameCell,
        contextMenuItems: [],
        ...{
            ...defaultSize,
            minWidth: 140,
            maxWidth: undefined
        }
    },
    {
        field: 'Not due',
        headerName: 'Ikke forfalt',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    },
    {
        field: '< 15',
        headerName: '< 15',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    },
    {
        field: '15-30',
        headerName: '15-30',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    },
    {
        field: '30-60',
        headerName: '30-60',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    },
    {
        field: '60-90',
        headerName: '60-90',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    },
    {
        field: '90-180',
        headerName: '90-180',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    }, {
        field: '> 180',
        headerName: '> 180',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    },
    {
        field: 'total',
        headerName: 'Total',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        valueFormatter,
        ...defaultSize
    }
])

const fetchData = async () => fetchAgeDividedReport(props.relationType, page?.value?.pageNumber ?? 0, 50).then(res => {
    report.value = res.content.map(entry => {
        page.value = res
        return entry
    })

})

const clearData = () => report.value = []

onMounted(async () => {
    await fetchData()
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION, ORG_SELECTED, SACC_LINES_UPDATED], async () => {
        clearData()
        await fetchData()
        gridApi.value?.redrawRows()
    })
})

onUnmounted(cleanUp)

</script>
